/* =============================================================================
 * Criptografia compativel com o backend Prisma.
 *
 * Porte fiel de LibGeral.CriptografarBase64 / DescriptografarBase64 /
 * CriptografarBase64Derivado (iPrisma\scripts\LibGeral.js:5158-5226).
 *
 * Isto NAO e' seguranca - e' ofuscacao. O objetivo aqui e' unico: gerar
 * exatamente os mesmos bytes que o servidor espera, principalmente no hash da
 * URL de arquivo (/file?p=...). Um caractere diferente e a imagem nao abre.
 * Por isso o algoritmo e' copiado, nao "melhorado".
 *
 * Diferenca em relacao ao original: o original usa Buffer (Node), que o browser
 * nao tem - o app antigo carregava um polyfill so' por causa disto. Aqui e'
 * btoa/atob nativo. Ver os dois comentarios marcados com [BUFFER] abaixo, que
 * sao onde a equivalencia precisa ser entendida.
 * ========================================================================== */

/**
 * XOR posicional com a chave, resultado em base64.
 *
 * [BUFFER] O original monta um array de inteiros `(c ^ k)` e chama
 * `Buffer.from(array, 'utf8')`. Quando recebe um array, o Buffer trata cada
 * elemento como um BYTE e trunca em 256 - o `& 0xff` aqui reproduz isso.
 * Sem ele, qualquer caractere acentuado no caminho do arquivo gera hash
 * diferente do servidor. Nao trocar por TextEncoder: UTF-8 mudaria os bytes.
 */
export function criptografarBase64(texto, chave) {
    if (!chave) throw new Error('Chave faltante em criptografarBase64');
    let binario = '';
    for (let i = 0; i < texto.length; i++) {
        const c = texto.charCodeAt(i);
        const k = chave.charCodeAt(i % chave.length);
        binario += String.fromCharCode((c ^ k) & 0xff);
    }
    return btoa(binario);
}

export function descriptografarBase64(textoCriptografado, chave) {
    if (!chave) throw new Error('Chave faltante em descriptografarBase64');
    const binario = atob(textoCriptografado);
    let saida = '';
    for (let i = 0; i < binario.length; i++) {
        saida += String.fromCharCode(binario.charCodeAt(i) ^ chave.charCodeAt(i % chave.length));
    }
    return saida;
}

/**
 * Salt derivado de um hash estavel da entrada: mesma entrada -> mesma saida.
 * E' o que torna a URL de imagem cacheavel (o service worker so' acerta o cache
 * se a mesma imagem gerar sempre a mesma URL).
 */
function saltDeterministicoDe(texto) {
    let h = 0;
    for (let i = 0; i < texto.length; i++) {
        h = (h * 31 + texto.charCodeAt(i)) & 0xffff;
    }
    return h.toString(16).padStart(4, '0').slice(0, 2); // 2 chars hex, mesmo charset do GUID
}

function saltAleatorio() {
    return crypto.randomUUID().slice(0, 2);
}

/**
 * Deriva uma chave a partir do salt e criptografa. O salt vai nos 2 primeiros
 * caracteres do retorno - e' de la' que JS e C# leem para descriptografar.
 *
 * [BUFFER] O original termina com:
 *     const b64 = Buffer.from(criptografado, 'base64');
 *     return salt + Buffer.from(b64, 'utf8').toString('base64');
 * Isso decodifica base64 e re-codifica: como a saida de btoa ja' e' canonica,
 * o round-trip e' IDENTIDADE. Ou seja, e' apenas `salt + criptografado`.
 * Verificado contra o iPrisma pelo teste de paridade em cripto.test.js.
 */
export function criptografarBase64Derivado(texto, chave, saltDeterministico = false) {
    if (!chave) throw new Error('Chave faltante em criptografarBase64Derivado');

    const salt = saltDeterministico ? saltDeterministicoDe(texto) : saltAleatorio();
    const chaveBase = btoa(chave).replaceAll('=', '').split('').reverse().join('') + salt;

    let chaveDerivada = '';
    let soma = salt.charCodeAt(0);
    for (let i = 0; i < chaveBase.length; i++) {
        const c = chaveBase.charCodeAt(i);
        soma += c;
        const xor = (soma % 255) ^ c;
        chaveDerivada += xor.toString(16).padStart(2, '0').toUpperCase();
    }
    chaveDerivada = chaveDerivada.split('').reverse().join('');

    return salt + criptografarBase64(texto, chaveDerivada);
}

export function descriptografarBase64Derivado(textoCriptografado, chave) {
    if (!chave) throw new Error('Chave faltante em descriptografarBase64Derivado');

    const salt = textoCriptografado.slice(0, 2);
    const conteudo = textoCriptografado.slice(2);
    const chaveBase = btoa(chave).replaceAll('=', '').split('').reverse().join('') + salt;

    let chaveDerivada = '';
    let soma = salt.charCodeAt(0);
    for (let i = 0; i < chaveBase.length; i++) {
        const c = chaveBase.charCodeAt(i);
        soma += c;
        const xor = (soma % 255) ^ c;
        chaveDerivada += xor.toString(16).padStart(2, '0').toUpperCase();
    }
    chaveDerivada = chaveDerivada.split('').reverse().join('');

    return descriptografarBase64(conteudo, chaveDerivada);
}
